import { z } from 'zod'
import type { CommentReply, ReviewComment } from '../../../../types/chat'
import { buildInsertReviewCommentsOptions } from '../../../../utils/word/review-comments'
import type { InsertReviewCommentsOptions } from '../../../word/word-service'
import {
  GetSectionArgsSchema,
  SkillInsertCommentsArgsSchema,
  SkillInsertHtmlArgsSchema,
  SkillRepliesArgsSchema,
} from './dtos/handlers'

export function zodIssueMessage(error: z.ZodError): string {
  const issue = error.issues[0]
  if (!issue) {
    return 'Invalid arguments'
  }
  const path = issue.path.length ? `${issue.path.join('.')}: ` : ''
  return `${path}${issue.message}`
}

export function toWordInsertLocation(
  location: 'before' | 'after' | 'replace'
): Word.InsertLocation {
  if (location === 'before') return Word.InsertLocation.before
  if (location === 'after') return Word.InsertLocation.after
  return Word.InsertLocation.replace
}

export function parseInsertHtmlArgs(
  args: Record<string, unknown>
): { ok: true; html: string; location: Word.InsertLocation } | { ok: false; error: string } {
  const parsed = SkillInsertHtmlArgsSchema.safeParse(args)
  if (!parsed.success) {
    return { ok: false, error: zodIssueMessage(parsed.error) }
  }
  return {
    ok: true,
    html: parsed.data.html,
    location: toWordInsertLocation(parsed.data.location),
  }
}

export function parseInsertCommentsArgs(
  args: Record<string, unknown>
):
  | { ok: true; comments: ReviewComment[]; options: InsertReviewCommentsOptions }
  | { ok: false; error: string } {
  const parsed = SkillInsertCommentsArgsSchema.safeParse(args)
  if (!parsed.success) {
    return { ok: false, error: zodIssueMessage(parsed.error) }
  }
  const { comments: dtos, selection_paragraph_ids, resolve_document_wide } = parsed.data
  return {
    ok: true,
    comments: dtos.map((dto) => ({
      paragraphId: dto.paragraph_id,
      commentText: dto.comment_text,
      searchText: dto.search_text ?? null,
    })),
    options: buildInsertReviewCommentsOptions({
      resolveDocumentWide: resolve_document_wide,
      selectionParagraphIds: selection_paragraph_ids ?? [],
    }),
  }
}

export function parseCommentRepliesArgs(
  args: Record<string, unknown>
): { ok: true; replies: CommentReply[] } | { ok: false; error: string } {
  const parsed = SkillRepliesArgsSchema.safeParse(args)
  if (!parsed.success) {
    return { ok: false, error: zodIssueMessage(parsed.error) }
  }
  return {
    ok: true,
    replies: parsed.data.replies.map((dto) => ({
      commentId: dto.comment_id,
      commentText: dto.comment_text,
    })),
  }
}

export function wordCommentBatchToSkillContent(result: {
  insertedCount: number
  commentIds: (string | null)[]
}) {
  return {
    inserted_count: result.insertedCount,
    comment_ids: result.commentIds,
  }
}

export function parseGetSectionArgs(
  args: Record<string, unknown>
):
  { ok: true; id: string; useHtml: boolean; perParagraph: boolean } | { ok: false; error: string } {
  const result = GetSectionArgsSchema.safeParse(args)
  if (!result.success) {
    return { ok: false, error: zodIssueMessage(result.error) }
  }
  const { id, use_html, per_paragraph } = result.data
  return { ok: true, id, useHtml: use_html, perParagraph: per_paragraph }
}
